import { Delete, Edit, StickyNote2 } from '@mui/icons-material';
import {
  createManualActionEditRoute,
  LP_MANUAL_ACTION_CREATE,
} from 'adminConstants/routes';
import Page from 'components/new/Page';
import PageFilters, { Filter } from 'components/new/PageFilters';
import { useLayoutContext } from 'contexts/LayoutContext';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column } from 'further-ui/components/Layout';
import SearchField from 'further-ui/components/SearchField';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { useDisclosure, usePaginatedFilters } from 'further-ui/hooks';
import { numberToCurrencyString } from 'further-ui/utils';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';

const pageId = 'list-manual-actions';

type Props = {
  vehicleId: string;
};

const ListManualActions: React.FC<Props> = ({ vehicleId }) => {
  const { vehicle } = useLayoutContext();
  const deleteDisclosure = useDisclosure<{ id: string }>();

  const { pagination, filters, handleChangeFilters, isFiltersChangePending } =
    usePaginatedFilters<
      {
        searchQuery: string;
      },
      'ledgerDate'
    >(pageId, {
      defaultFilters: {
        searchQuery: '',
      },
      defaultOrder: {
        orderBy: 'ledgerDate',
        order: 'desc',
      },
    });

  const [data] = api.manualJournal.listManualActions.useSuspenseQuery({
    vehicleId,
    searchQuery: filters.searchQuery,
    ...pagination.properties,
  });

  const deleteManualAction = api.manualJournal.deleteManualAction.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Manual action deleted successfully.',
    }),
  );

  const handleDeleteManualAction = () => {
    if (deleteManualAction.isPending) {
      return;
    }
    deleteManualAction.mutate({
      manualActionId: deleteDisclosure.actionProps.id,
      vehicleId,
    });
    deleteDisclosure.onClose();
  };

  const columns: Array<TableColumn<(typeof data.results)[number]>> = [
    {
      label: 'Description',
      key: 'description',
    },
    {
      label: 'Ledger date',
      key: 'ledgerDate',
      render: (row) => row.ledgerDate.toReadable(),
    },

    {
      label: 'Amount',
      key: 'totalAmountLcy',
      render: (row) =>
        numberToCurrencyString(row.totalAmountLcy, {
          currency: vehicle?.baseCurrency,
        }),
    },
  ];

  return (
    <Page
      spacing="lg"
      heading="Manual actions"
      withHeadingDivider
      headingIcon={StickyNote2}
      actions={[
        {
          label: 'Add manual action',
          link: LP_MANUAL_ACTION_CREATE,
          variant: 'contained',
        },
      ]}
    >
      <Column spacing="sm">
        <PageFilters justify="space-between">
          <Filter size="md">
            <SearchField
              autoSearchOnDebounce
              name="searchQuery"
              placeholder="Search by description"
              fullWidth
              value={filters.searchQuery}
              onSearch={(searchQuery) => {
                handleChangeFilters({ searchQuery });
              }}
              allowEmptyQuery
              iconPlacement="left"
            />
          </Filter>
        </PageFilters>
        <Table
          columns={columns}
          isLoading={isFiltersChangePending}
          pagination={pagination}
          rows={data.results}
          rowsTotalCount={data.totalCount}
          emptyMessage="No manual actions found."
          actions={[
            {
              label: 'Edit',
              icon: Edit,
              link: (row) => createManualActionEditRoute(row.id),
              disabled: (row) => !row.canModify,
              tooltip: (row) =>
                !row.canModify ? row.modifyAbilityReason : undefined,
            },
            {
              label: 'Delete',
              icon: Delete,
              onClick: (row) => {
                deleteDisclosure.stageAction({ id: row.id });
              },

              disabled: (row) => !row.canModify || deleteManualAction.isPending,
              tooltip: (row) =>
                !row.canModify ? row.modifyAbilityReason : undefined,
            },
          ]}
        />
      </Column>
      <ConfirmDialog
        maxWidth="sm"
        onClose={deleteDisclosure.onClose}
        open={deleteDisclosure.isOpen}
        title="Are you sure you wish to delete this action?"
        content="This cannot be undone and all information relating to the action will be lost. If you have draft equalisations they will also be deleted."
        actions={{
          confirm: {
            label: 'Delete manual action',
            onClick: handleDeleteManualAction,
          },
          cancel: {
            label: 'Go back',
            onClick: deleteDisclosure.onClose,
          },
        }}
      />
    </Page>
  );
};

const ListManualActionsWrapper: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();

  if (!selectedVehicleId) {
    return null;
  }

  return <ListManualActions vehicleId={selectedVehicleId} />;
};

export default ListManualActionsWrapper;
